import { useLingui } from '@lingui/react/macro'
import { AgenticMarkdown } from 'designSystem/AgenticMarkdown'
import React, { useMemo } from 'react'
import type { Components, Options } from 'react-markdown'
import styled from 'styled-components'

import { remarkTagLinks, TAG_LINK_PREFIX } from './tagMentions'
import { useTagMention } from './useTagMention'

export const TagMention = styled.a<{ $onChart: boolean }>`
  color: inherit;
  text-decoration: underline;
  text-decoration-style: ${({ $onChart }) => ($onChart ? 'solid' : 'dotted')};
  text-underline-offset: 0.15em;
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

/**
 * The reply, with every tag it mentions underlined: dotted when a click would
 * add it to the chart, solid when it is already there and hovering lights its
 * pen. Falls back to the plain renderer while the site's tag list is empty.
 */
export const TagLinkedMarkdown: React.FC<{ children: string }> = ({
  children,
}) => {
  const { t } = useLingui()
  const { index, isOnChart, hover, release, add } = useTagMention()
  const remarkPlugins: Options['remarkPlugins'] = useMemo(
    () => (index.size ? [[remarkTagLinks, { index }]] : []),
    [index]
  )
  const components: Components | undefined = useMemo(() => {
    if (!index.size) return undefined
    return {
      a: ({ href, title, children: linkChildren }) => {
        if (!href?.startsWith(TAG_LINK_PREFIX)) {
          return <a href={href}>{linkChildren}</a>
        }
        const id = decodeURIComponent(href.slice(TAG_LINK_PREFIX.length))
        const onChart = isOnChart(id)
        return (
          <TagMention
            href={href}
            $onChart={onChart}
            title={
              onChart ? t`${id} · on the chart` : t`${id} · add to the chart`
            }
            onMouseEnter={() => hover(id)}
            onMouseLeave={release}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              add(id)
            }}>
            {title && !linkChildren ? title : linkChildren}
          </TagMention>
        )
      },
    }
  }, [add, hover, index.size, isOnChart, release, t])
  if (!index.size) return <AgenticMarkdown>{children}</AgenticMarkdown>
  return (
    <AgenticMarkdown remarkPlugins={remarkPlugins} components={components}>
      {children}
    </AgenticMarkdown>
  )
}
